import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Tooltip, Input, Button, Switch, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import s from './s.module.less';
import { ISpecialtyBidDisplay, BudgetPeriod } from 'types/marketing';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import { MentalHealthBid, DefaultSpecialtyBid } from 'hooks/marketing/useBudgetInfo';

interface SpecialtyBidOverridesProps {
    bidPerBooking: number;
    budgetCap?: number;
    budgetPeriod?: BudgetPeriod;
    specialtyBids?: ISpecialtyBidDisplay[];
    onChange: (data: ISpecialtyBidDisplay[]) => void;
    onValidationChange?: (errors: Record<string, string>) => void;
    disabled?: boolean;
    isOps?: boolean;
    providerId?: number;
}

const SpecialtyBidOverrides = ({
    bidPerBooking,
    budgetCap = 0,
    budgetPeriod,
    specialtyBids,
    onChange,
    onValidationChange,
    disabled = false,
    isOps = false,
    providerId,
}: SpecialtyBidOverridesProps) => {
    const [providerKlarityServiceList, serviceListLoading] = useProviderKlarityServiceList(isOps, providerId);
    const [localValues, setLocalValues] = useState<Record<string, number>>();
    const [capValues, setCapValues] = useState<Record<string, number>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [hasUserChanges, setHasUserChanges] = useState(false);
    const [expandedSpecs, setExpandedSpecs] = useState<Set<string>>(new Set());
    const [disabledSpecs, setDisabledSpecs] = useState<Set<string>>(new Set());
    const prevBidRef = useRef<number | null>(null);
    const prevBudgetCapRef = useRef<number | null>(null);
    const errorTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    const capLabel = budgetPeriod === BudgetPeriod.WEEKLY ? 'Weekly cap' : 'Monthly cap';
    const budgetLabel = budgetPeriod === BudgetPeriod.WEEKLY ? 'weekly budget' : 'monthly budget';

    const buildBidPayload = (
        bidVals: Record<string, number>,
        caps: Record<string, number>,
        disabledSet: Set<string>,
    ): ISpecialtyBidDisplay[] =>
        Object.entries(bidVals).map(([spec, bidAmount]) => ({
            specialtyName: spec,
            bidAmount,
            specialtyCap: caps[spec],
            enabled: !disabledSet.has(spec),
        }));

    const isOverridden = useCallback(
        (specialty: string) => {
            return !!(localValues?.[specialty] && localValues?.[specialty] > bidPerBooking);
        },
        [localValues, bidPerBooking],
    );

    const specialties = useMemo(() => {
        if (!providerKlarityServiceList) return [];
        const uniqueSpecialtiesMap = new Map<string, { specialtyName: string; logo: string }>();

        providerKlarityServiceList.forEach((item) => {
            if (item.serviceSpecialtyName && !uniqueSpecialtiesMap.has(item.serviceSpecialtyName)) {
                uniqueSpecialtiesMap.set(item.serviceSpecialtyName, {
                    specialtyName: item.serviceSpecialtyName,
                    logo: item.logo || '',
                });
            }
        });

        return Array.from(uniqueSpecialtiesMap.values());
    }, [providerKlarityServiceList]);

    useEffect(() => {
        const isInitialized = localValues !== undefined;
        const isSameValues =
            isInitialized && !!specialtyBids?.every((item) => item.bidAmount === localValues?.[item.specialtyName]);
        if (specialties.length === 0 || isSameValues) return;
        const initValues: Record<string, number> = {};
        const initCaps: Record<string, number> = {};

        const initDisabled = new Set<string>();

        if (specialtyBids && specialtyBids.length > 0) {
            specialties.forEach((specialty) => {
                const existingBid = specialtyBids.find((item) => item.specialtyName === specialty.specialtyName);
                initValues[specialty.specialtyName] = existingBid ? existingBid.bidAmount : bidPerBooking;
                if (existingBid?.specialtyCap) {
                    initCaps[specialty.specialtyName] = existingBid.specialtyCap;
                }
                if (existingBid?.enabled === false) {
                    initDisabled.add(specialty.specialtyName);
                }
            });
            if (specialtyBids.find((item) => item.bidAmount !== bidPerBooking)) {
                setHasUserChanges(true);
            }
        } else {
            specialties.forEach((specialty) => {
                initValues[specialty.specialtyName] =
                    specialty.specialtyName === 'Psychiatric & Mental Health' ? MentalHealthBid : DefaultSpecialtyBid;
            });
        }
        setLocalValues(initValues);
        setCapValues(initCaps);
        setDisabledSpecs(initDisabled);
        prevBidRef.current = bidPerBooking;
        prevBudgetCapRef.current = budgetCap;
        onChange(buildBidPayload(initValues, initCaps, initDisabled));
    }, [specialtyBids, specialties]);

    useEffect(() => {
        if (!localValues || Object.keys(localValues).length === 0) return;
        if (prevBidRef.current === bidPerBooking) return;
        const prevBid = prevBidRef.current;
        prevBidRef.current = bidPerBooking;
        if (prevBid !== null && bidPerBooking > prevBid) {
            setLocalValues((prev) => {
                if (!prev) return prev;
                let changed = false;
                const next: Record<string, number> = {};
                Object.entries(prev).forEach(([k, v]) => {
                    if (v < bidPerBooking) {
                        next[k] = bidPerBooking;
                        changed = true;
                    } else {
                        next[k] = v;
                    }
                });
                if (changed) {
                    onChange(buildBidPayload(next, capValues, disabledSpecs));
                }
                return changed ? next : prev;
            });
        }
    }, [bidPerBooking]);

    useEffect(() => {
        if (prevBudgetCapRef.current === budgetCap) return;
        const prevCap = prevBudgetCapRef.current;
        prevBudgetCapRef.current = budgetCap;
        if (prevCap !== null && budgetCap < prevCap && budgetCap > 0) {
            setCapValues((prev) => {
                let changed = false;
                const next: Record<string, number> = {};
                Object.entries(prev).forEach(([k, v]) => {
                    if (v > budgetCap) {
                        next[k] = budgetCap;
                        changed = true;
                    } else {
                        next[k] = v;
                    }
                });
                if (changed && localValues) {
                    onChange(buildBidPayload(localValues, next, disabledSpecs));
                }
                return changed ? next : prev;
            });
        }
    }, [budgetCap]);

    const getOverrideCount = () => {
        return specialties.filter(
            (specialty) => !disabledSpecs.has(specialty.specialtyName) && isOverridden(specialty.specialtyName),
        ).length;
    };

    const setTipError = (key: string, msg: string) => {
        setErrors((prev) => ({ ...prev, [key]: msg }));
        if (errorTimers.current[key]) clearTimeout(errorTimers.current[key]);
        errorTimers.current[key] = setTimeout(() => {
            setErrors((prev) => {
                if (!prev[key]) return prev;
                const next = { ...prev };
                delete next[key];
                return next;
            });
            delete errorTimers.current[key];
        }, 2500);
    };

    const clearTipError = (key: string) => {
        if (errorTimers.current[key]) {
            clearTimeout(errorTimers.current[key]);
            delete errorTimers.current[key];
        }
        setErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleInputChange = (specialty: string, value: string) => {
        const numValue = parseFloat(value) || 0;
        const newValues = { ...localValues, [specialty]: numValue };
        setLocalValues(newValues);
        setHasUserChanges(true);
        clearTipError(`bid:${specialty}`);
        onChange(buildBidPayload(newValues, capValues, disabledSpecs));
    };

    const handleBidBlur = (specialty: string) => {
        const val = localValues?.[specialty];
        if (val == null) return;
        if (val < bidPerBooking) {
            const newValues = { ...localValues, [specialty]: bidPerBooking };
            setLocalValues(newValues);
            setTipError(`bid:${specialty}`, `Can't be below $${bidPerBooking.toLocaleString()} campaign bid`);
            onChange(buildBidPayload(newValues, capValues, disabledSpecs));
        }
    };

    const handleCapChange = (specialty: string, value: string) => {
        const numValue = value ? parseFloat(value) || 0 : undefined;
        const newCaps = { ...capValues };
        if (numValue !== undefined && numValue > 0) {
            newCaps[specialty] = numValue;
        } else {
            delete newCaps[specialty];
        }
        setCapValues(newCaps);
        clearTipError(`cap:${specialty}`);
        if (localValues) {
            onChange(buildBidPayload(localValues, newCaps, disabledSpecs));
        }
    };

    const handleCapBlur = (specialty: string) => {
        const val = capValues[specialty];
        if (!val) return;
        if (budgetCap > 0 && val > budgetCap) {
            const newCaps = { ...capValues, [specialty]: budgetCap };
            setCapValues(newCaps);
            setTipError(`cap:${specialty}`, `Can't exceed $${budgetCap.toLocaleString()} ${budgetLabel}`);
            if (localValues) {
                onChange(buildBidPayload(localValues, newCaps, disabledSpecs));
            }
        }
    };

    const handleButtonClick = (specialty: string, increment: number) => {
        const currentValue = localValues?.[specialty] || bidPerBooking;
        const newValue = Math.max(bidPerBooking, currentValue + increment);
        handleInputChange(specialty, newValue.toString());
    };

    const handleResetToEqual = () => {
        const resetValues: Record<string, number> = {};
        specialties.forEach((specialty) => {
            resetValues[specialty.specialtyName] = bidPerBooking;
        });
        setLocalValues(resetValues);
        setCapValues({});
        setErrors({});
        setHasUserChanges(false);
        onChange(buildBidPayload(resetValues, {}, disabledSpecs));
    };

    const toggleExpand = (specialtyName: string) => {
        setExpandedSpecs((prev) => {
            const next = new Set(prev);
            if (next.has(specialtyName)) {
                next.delete(specialtyName);
            } else {
                next.add(specialtyName);
            }
            return next;
        });
    };

    const handleToggleSpec = (specialtyName: string, checked: boolean) => {
        setDisabledSpecs((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.delete(specialtyName);
            } else {
                next.add(specialtyName);
                setExpandedSpecs((ep) => {
                    const ne = new Set(ep);
                    ne.delete(specialtyName);
                    return ne;
                });
            }
            if (localValues) {
                onChange(buildBidPayload(localValues, capValues, next));
            }
            return next;
        });
    };

    const renderSpecialtyRow = (specialty: { specialtyName: string; logo: string }) => {
        const value =
            localValues?.[specialty.specialtyName] !== undefined ? localValues[specialty.specialtyName] : bidPerBooking;
        const isExpanded = expandedSpecs.has(specialty.specialtyName);
        const isToggleOff = disabledSpecs.has(specialty.specialtyName);
        const bidErrKey = `bid:${specialty.specialtyName}`;
        const capErrKey = `cap:${specialty.specialtyName}`;

        return (
            <div key={specialty.specialtyName} className={s.specRowCard}>
                <div className={s.specRowMain}>
                    <span className={s.specRowIcon}>
                        <img src={specialty.logo} alt={specialty.specialtyName} className={s.specRowImg} />
                    </span>
                    <div className={s.specRowInfo}>
                        <div className={s.specRowName}>{specialty.specialtyName}</div>
                    </div>
                    <div className={s.specRowField}>
                        <span className={s.specRowFieldLabel}>Bid / booking</span>
                        <Input
                            prefix="$"
                            value={value}
                            onChange={(e) => handleInputChange(specialty.specialtyName, e.target.value)}
                            onBlur={() => handleBidBlur(specialty.specialtyName)}
                            className={`${s.specRowInput} ${errors[bidErrKey] ? s.inputError : ''}`}
                            type="number"
                            disabled={disabled || isToggleOff}
                        />
                        {errors[bidErrKey] && <div className={s.errorTip}>{errors[bidErrKey]}</div>}
                    </div>
                    <div className={s.specRowField}>
                        <span className={s.specRowFieldLabel}>{capLabel}</span>
                        <Input
                            prefix="$"
                            placeholder="Auto"
                            value={capValues[specialty.specialtyName] ?? ''}
                            onChange={(e) => handleCapChange(specialty.specialtyName, e.target.value)}
                            onBlur={() => handleCapBlur(specialty.specialtyName)}
                            className={`${s.specRowCapInput} ${errors[capErrKey] ? s.inputError : ''}`}
                            type="number"
                            disabled={disabled || isToggleOff}
                        />
                        {errors[capErrKey] && <div className={s.errorTip}>{errors[capErrKey]}</div>}
                    </div>
                    <Switch
                        checked={!disabledSpecs.has(specialty.specialtyName)}
                        disabled={disabled}
                        size="small"
                        onChange={(checked) => handleToggleSpec(specialty.specialtyName, checked)}
                    />
                    <button
                        type="button"
                        className={`${s.specRowExpandBtn} ${isExpanded ? s.specRowExpandBtnOpen : ''}`}
                        onClick={() => toggleExpand(specialty.specialtyName)}
                        disabled={disabled || isToggleOff}
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                        >
                            <path d="M2 4.5L6 8.5L10 4.5" />
                        </svg>
                    </button>
                </div>

                {isExpanded && !isToggleOff && (
                    <div className={s.specRowExpanded}>
                        <div className={s.specRowQuickLabel}>
                            Quick presets — adjust this specialty&apos;s bid relative to the base
                        </div>
                        <div className={s.specRowQuickChips}>
                            <Button
                                size="small"
                                onClick={() => handleButtonClick(specialty.specialtyName, 10)}
                                disabled={disabled}
                            >
                                +$10
                            </Button>
                            <Button
                                size="small"
                                onClick={() => handleButtonClick(specialty.specialtyName, 20)}
                                disabled={disabled}
                            >
                                +$20
                            </Button>
                            <Button
                                size="small"
                                onClick={() => handleButtonClick(specialty.specialtyName, 30)}
                                disabled={disabled}
                            >
                                +$30
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const overrideCount = getOverrideCount();

    if (isOps) {
        return (
            <Spin spinning={serviceListLoading}>
                <div className={s.container}>
                    <div className={s.header}>
                        <div className={s.titleSection}>
                            <span className={s.title}>Specialty bid overrides</span>
                            <Tooltip
                                title="Set custom bids for any specialty you want to prioritize. Overrides apply only to that specialty; others keep your general bid."
                                color="#fff"
                                overlayInnerStyle={{ color: '#333', padding: 8 }}
                            >
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                        <div className={s.controls}>
                            {overrideCount > 0 && (
                                <span className={s.overrideCount}>Active overrides: {overrideCount}</span>
                            )}
                            {hasUserChanges && (
                                <Button className={s.resetButton} onClick={handleResetToEqual} disabled={disabled}>
                                    Reset to Equal
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={s.specRowList}>{specialties.map((specialty) => renderSpecialtyRow(specialty))}</div>
                </div>
            </Spin>
        );
    }

    return (
        <Spin spinning={serviceListLoading}>
            <div className={s.rowContainer}>
                <div className={s.rowHeader}>
                    <div className={s.rowTitleSection}>
                        <span className={s.rowTitle}>Override bids by specialty</span>
                        <Tooltip
                            title="Set custom bids for any specialty you want to prioritize. Overrides apply only to that specialty; others keep your general bid."
                            color="#fff"
                            overlayInnerStyle={{ color: '#333', padding: 8 }}
                        >
                            <QuestionCircleOutlined className={s.rowInfoIcon} />
                        </Tooltip>
                    </div>
                    <div className={s.rowControls}>
                        {overrideCount > 0 && (
                            <span className={s.rowOverrideCount}>Active overrides: {overrideCount}</span>
                        )}
                        {hasUserChanges && (
                            <Button size="small" onClick={handleResetToEqual} disabled={disabled}>
                                Reset to Equal
                            </Button>
                        )}
                    </div>
                </div>
                <div className={s.rowDesc}>
                    Set a bid per specialty, then expand any row to adjust with quick presets. Specialties without an
                    override use your default bid above.
                </div>
                <div className={s.specRowList}>{specialties.map((specialty) => renderSpecialtyRow(specialty))}</div>
            </div>
        </Spin>
    );
};

export default SpecialtyBidOverrides;
